import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Check, RotateCcw, X } from 'lucide-react';
import { warpPerspective, enhanceCanvas, estimateOutputSize } from '../utils/documentCrop';

const CORNER_LABELS = ['أعلى يسار', 'أعلى يمين', 'أسفل يمين', 'أسفل يسار'];

/**
 * نافذة تحديد زوايا المستند: تعرض الصورة مع 4 نقاط قابلة للسحب، المستخدم
 * يحاذيها مع زوايا الورقة الفعلية، ثم يضغط "تم" فيتم تصحيح المنظور
 * وتسطيح المستند بالكامل داخل المتصفح (بدون أي معالجة على السيرفر).
 *
 * props:
 *   open, onClose
 *   imageSrc: data URL للصورة الأصلية
 *   onConfirm: (croppedDataUrl) => void
 */
export default function DocumentCropperModal({ open, onClose, imageSrc, onConfirm }) {
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [corners, setCorners] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [processing, setProcessing] = useState(false);

  const resetCorners = useCallback(() => {
    const inset = 8;
    setCorners([
      { xPct: inset, yPct: inset },
      { xPct: 100 - inset, yPct: inset },
      { xPct: 100 - inset, yPct: 100 - inset },
      { xPct: inset, yPct: 100 - inset },
    ]);
  }, []);

  useEffect(() => {
    if (open) {
      setImgLoaded(false);
      resetCorners();
    }
  }, [open, imageSrc, resetCorners]);

  const handlePointerDown = (index) => (e) => {
    e.preventDefault();
    setDragIndex(index);
  };

  const handlePointerMove = (e) => {
    if (dragIndex === null || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let xPct = ((clientX - rect.left) / rect.width) * 100;
    let yPct = ((clientY - rect.top) / rect.height) * 100;
    xPct = Math.max(0, Math.min(100, xPct));
    yPct = Math.max(0, Math.min(100, yPct));
    setCorners((prev) => {
      const next = [...prev];
      next[dragIndex] = { xPct, yPct };
      return next;
    });
  };

  const handlePointerUp = () => setDragIndex(null);

  const handleConfirm = async () => {
    if (!imgRef.current || !corners) return;
    setProcessing(true);
    try {
      const img = imgRef.current;
      const naturalCorners = corners.map((c) => ({
        x: (c.xPct / 100) * img.naturalWidth,
        y: (c.yPct / 100) * img.naturalHeight,
      }));
      const { width, height } = estimateOutputSize(naturalCorners);
      const canvas = warpPerspective(img, naturalCorners, width, height);
      enhanceCanvas(canvas);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      onConfirm(dataUrl);
    } catch (err) {
      console.error('Document crop failed', err);
      onConfirm(imageSrc);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" dir="rtl">
        <DialogHeader>
          <DialogTitle>حاذِ زوايا الورقة</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500 -mt-2">
          اسحب النقاط الأربع لتطابق زوايا الفاتورة بالضبط، ثم اضغط "تم" لتسطيحها.
        </p>

        <div
          ref={containerRef}
          className="relative select-none touch-none bg-black rounded-lg overflow-hidden"
          onMouseMove={handlePointerMove}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt="فاتورة"
            className="w-full h-auto block"
            onLoad={() => setImgLoaded(true)}
            draggable={false}
          />

          {imgLoaded && corners && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ zIndex: 5 }}
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <polygon
                points={corners.map((c) => `${c.xPct},${c.yPct}`).join(' ')}
                fill="rgba(16,185,129,0.2)"
                stroke="rgb(16,185,129)"
                strokeWidth="0.5"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          )}

          {imgLoaded && corners && corners.map((c, i) => (
            <div
              key={i}
              onMouseDown={handlePointerDown(i)}
              onTouchStart={handlePointerDown(i)}
              className="absolute w-8 h-8 -mt-4 -ms-4 rounded-full bg-emerald-500 border-2 border-white shadow-lg cursor-move flex items-center justify-center text-white text-xs font-bold"
              style={{ left: `${c.xPct}%`, top: `${c.yPct}%`, zIndex: 10, touchAction: 'none' }}
              title={CORNER_LABELS[i]}
            >
              {i + 1}
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            <X className="w-4 h-4 me-1" /> إلغاء
          </Button>
          <Button type="button" variant="outline" onClick={resetCorners} className="flex-1">
            <RotateCcw className="w-4 h-4 me-1" /> إعادة ضبط
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={processing} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
            <Check className="w-4 h-4 me-1" /> {processing ? 'جارٍ...' : 'تم'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
